/**
 * Document Download Hook
 *
 * Wraps the getDownloadUrl Convex query and provides
 * helper functions for download and view-in-new-tab actions.
 * Convex signed URLs are temporary so the hook fetches on demand.
 */

import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import { useWorkspaceQuery } from "@/hooks";

interface UseDocumentDownloadReturn {
  url: string | null;
  fileName: string | null;
  fileType: string | null;
  isLoading: boolean;
  download: () => Promise<void>;
  viewInNewTab: () => void;
}

export function useDocumentDownload(
  documentId: Id<"documents"> | null,
): UseDocumentDownloadReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.getDownloadUrl,
    documentId ? { id: documentId } : "skip",
  );

  const url = data?.url ?? null;
  const fileName = data?.fileName ?? null;
  const fileType = data?.fileType ?? null;

  async function download() {
    if (!url || !fileName) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    }
  }

  function viewInNewTab() {
    if (!url) return;
    window.open(url, "_blank");
  }

  return { url, fileName, fileType, isLoading, download, viewInNewTab };
}
